import React, { useState, useEffect, useCallback } from "react";
import "./matrimonyList.css";
import { useSelector } from "react-redux";
import { Avatar } from "@mui/material";
import ProfileDetailsModal from "./profileDetailsModal.tsx";

import Notifications from "../notification/notifications.tsx";
import PaymentActivationModal from "./paymentActivationModal.tsx";

import { checkProfileAccess, sendInterest, getSentRequests } from "../../../services/connectionAPI.ts";

import { getAllUsers } from "../../../services/authAPI.js";

import {
  CASTE_OPTIONS,
  COUNTRY_OPTIONS,
  MARITAL_STATUS_OPTIONS,
  RELIGION_OPTIONS,
} from "../../../utils/selectOptions.js";

const DEFAULT_AGE_FROM = 18;
const DEFAULT_AGE_TO = 60;
const DEFAULT_SORT = "Latest First";
const ITEMS_PER_PAGE = 10;

const MatrimonyList = () => {
  const user = useSelector((state: any) => state.auth.user);

  const [profiles, setProfiles] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [lookingFor, setLookingFor] = useState<"male" | "female" | null>(null);

  const [ageFrom, setAgeFrom] = useState(DEFAULT_AGE_FROM);
  const [ageTo, setAgeTo] = useState(DEFAULT_AGE_TO);

  const [sortBy, setSortBy] = useState(DEFAULT_SORT);

  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [selectedReligions, setSelectedReligions] = useState<string[]>([]);
  const [selectedCastes, setSelectedCastes] = useState<string[]>([]);
  const [selectedMaritalStatuses, setSelectedMaritalStatuses] = useState<string[]>([]);
  const [successMessage, setSuccessMessage] = useState("");
  // Profile access status
  const [hasAccessMap, setHasAccessMap] = useState<Record<number, boolean>>({});

  const [interestStatus, setInterestStatus] = useState<Record<number, string>>({});

  // Interest sending loading state
  const [sendingId, setSendingId] = useState<number | null>(null);

  const [openPaymentModal, setOpenPaymentModal] = useState(false);

  const [showFilters, setShowFilters] = useState(false);

  const [openModal, setOpenModal] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<any>(null);

  const [currentPage, setCurrentPage] = useState(1);

  // ---------------------------------------------------------
  // CLOSE PROFILE MODAL
  // ---------------------------------------------------------

  const handleCloseProfile = () => {
    setOpenModal(false);
    setSelectedProfile(null);
  };

  useEffect(() => {
    if (!user?.id) return;

    const loadSentRequests = async () => {
      try {
        const data = await getSentRequests();

        console.log("Sent Requests:", data);

        const statusMap: Record<number, string> = {};

        data?.requests?.forEach((request: any) => {
          // Only process requests sent by the logged-in user
          if (Number(request.sender_id) === Number(user.id)) {
            statusMap[Number(request.receiver_id)] = request.status;
          }
        });

        setInterestStatus(statusMap);
      } catch (error) {
        console.error("Failed to load sent requests:", error);
      }
    };

    loadSentRequests();
  }, [user?.id]);

  // ---------------------------------------------------------
  // CHECK PROFILE ACCESS
  // ---------------------------------------------------------

  const checkAccess = useCallback(async (userId: number) => {
    try {
      const res = await checkProfileAccess(userId);

      setHasAccessMap((prev) => {
        if (prev[userId] === res.canView) {
          return prev;
        }

        return {
          ...prev,
          [userId]: res.canView,
        };
      });
    } catch (err) {
      console.error("Access check failed", err);
    }
  }, []);

  // ---------------------------------------------------------
  // OPEN PROFILE
  // ---------------------------------------------------------

  const handleOpenProfile = async (profile: any) => {
    setSelectedProfile(profile);
    setOpenModal(true);

    // Re-check access before displaying profile
    await checkAccess(profile.id);
  };

  // ---------------------------------------------------------
  // AGE CALCULATION
  // ---------------------------------------------------------

  const calculateAgeNum = (dob?: string): number => {
    if (!dob) return 0;

    const birthDate = new Date(dob);
    const today = new Date();

    let age = today.getFullYear() - birthDate.getFullYear();

    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age;
  };

  const calculateAge = (dob?: string): string | number => (!dob ? "-" : calculateAgeNum(dob));

  // ---------------------------------------------------------
  // GET INITIAL
  // ---------------------------------------------------------

  const getInitial = (name?: string) => (name ? name.charAt(0).toUpperCase() : "?");

  // ---------------------------------------------------------
  // SORT PROFILES
  // ---------------------------------------------------------

  const sortProfiles = useCallback((data: any[], sort: string) => {
    const sorted = [...data];

    if (sort === "Age: High to Low") {
      sorted.sort((a, b) => calculateAgeNum(b.dob) - calculateAgeNum(a.dob));
    } else if (sort === "Age: Low to High") {
      sorted.sort((a, b) => calculateAgeNum(a.dob) - calculateAgeNum(b.dob));
    } else {
      sorted.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    }

    return sorted;
  }, []);

  // ---------------------------------------------------------
  // LOAD USERS
  // ---------------------------------------------------------

  const loadUsers = useCallback(async (filters: {
    gender: "male" | "female" | null;
    ageFrom: number;
    ageTo: number;
    countries: string[];
    religions: string[];
    castes: string[];
    maritalStatuses: string[];
  }) => {
    try {
      setLoading(true);

      const params: Record<string, string> = {
        ageFrom: String(filters.ageFrom),
        ageTo: String(filters.ageTo),
      };

      if (filters.gender !== null) {
        params.gender = filters.gender;
      }

      if (filters.countries.length === 1) {
        params.country = filters.countries[0];
      }

      if (filters.religions.length === 1) {
        params.religion = filters.religions[0];
      }

      if (filters.castes.length === 1) {
        params.caste = filters.castes[0];
      }

      if (filters.maritalStatuses.length === 1) {
        params.marital_status = filters.maritalStatuses[0];
      }

      const data = await getAllUsers(params);

      let users: any[] = (data?.users || []).filter((u: any) => u.profile_status === "active");

      // Multiple country filtering
      if (filters.countries.length > 1) {
        users = users.filter((u) => filters.countries.includes(u.resident_country));
      }

      // Multiple religion filtering
      if (filters.religions.length > 1) {
        users = users.filter((u) => filters.religions.includes(u.religion));
      }

      // Multiple caste filtering
      if (filters.castes.length > 1) {
        users = users.filter((u) => filters.castes.includes(u.caste));
      }

      // Multiple marital status filtering
      if (filters.maritalStatuses.length > 1) {
        users = users.filter((u) => filters.maritalStatuses.includes(u.marital_status));
      }

      // Sort
      users = sortProfiles(users, sortBy);

      setProfiles(users);

      setCurrentPage(1);

      // Check profile access
      users.forEach((u) => {
        checkAccess(u.id);
      });
    } catch (err) {
      console.error("Failed to load users", err);
    } finally {
      setLoading(false);
    }
  }, [checkAccess, sortBy, sortProfiles]);

  // ---------------------------------------------------------
  // LOAD USERS WHEN FILTERS CHANGE
  // ---------------------------------------------------------

  useEffect(() => {
    loadUsers({
      gender: lookingFor,
      ageFrom,
      ageTo,
      countries: selectedCountries,
      religions: selectedReligions,
      castes: selectedCastes,
      maritalStatuses: selectedMaritalStatuses,
    });
  }, [loadUsers, lookingFor, ageFrom, ageTo, selectedCountries, selectedReligions, selectedCastes, selectedMaritalStatuses]);

  // ---------------------------------------------------------
  // SORT WHEN SORT OPTION CHANGES
  // ---------------------------------------------------------

  useEffect(() => {
    setProfiles((prev) => sortProfiles(prev, sortBy));
  }, [sortBy, sortProfiles]);

  // ---------------------------------------------------------
  // SEND INTEREST
  // ---------------------------------------------------------

  const handleSendInterest = async (receiverId: number) => {
    try {
      // User profile must be active
      if (user?.profile_status !== "active") {
        setOpenPaymentModal(true);
        return;
      }

      // Prevent sending interest to own profile
      if (Number(user?.id) === Number(receiverId)) {
        return;
      }

      // Prevent duplicate pending request
      if (interestStatus[receiverId] === "pending") {
        return;
      }

      setSendingId(receiverId);

      // Send request to backend
      await sendInterest(receiverId);

      // Immediately update UI
      setInterestStatus((prev) => ({
        ...prev,
        [receiverId]: "pending",
      }));

      // Keep profile locked until request is accepted
      setHasAccessMap((prev) => ({
        ...prev,
        [receiverId]: false,
      }));

      setSuccessMessage("Interest sent successfully ❤️");

      setTimeout(() => {
        setSuccessMessage("");
      }, 3000);
    } catch (err: any) {
      console.error("Failed to send interest:", err);

      alert(err?.response?.data?.message || "Failed to send request");
    } finally {
      setSendingId(null);
    }
  };

  // ---------------------------------------------------------
  // FILTER HELPERS
  // ---------------------------------------------------------

  const isFilterActive =
    lookingFor !== null ||
    ageFrom !== DEFAULT_AGE_FROM ||
    ageTo !== DEFAULT_AGE_TO ||
    selectedCountries.length > 0 ||
    selectedReligions.length > 0 ||
    selectedCastes.length > 0 ||
    selectedMaritalStatuses.length > 0;

  const toggleItem = (setter: React.Dispatch<React.SetStateAction<string[]>>, value: string) =>
    setter((prev) => (prev.includes(value) ? prev.filter((x) => x !== value) : [...prev, value]));

  // ---------------------------------------------------------
  // CLEAR FILTERS
  // ---------------------------------------------------------

  const clearFilters = () => {
    setLookingFor(null);
    setAgeFrom(DEFAULT_AGE_FROM);
    setAgeTo(DEFAULT_AGE_TO);
    setSelectedCountries([]);
    setSelectedReligions([]);
    setSelectedCastes([]);
    setSelectedMaritalStatuses([]);
    setSortBy(DEFAULT_SORT);
  };

  // ---------------------------------------------------------
  // CHECK FILTER STATUS
  // ---------------------------------------------------------

  // ---------------------------------------------------------
  // PAGINATION
  // ---------------------------------------------------------

  const totalPages = Math.ceil(profiles.length / ITEMS_PER_PAGE);

  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;

  const endIndex = startIndex + ITEMS_PER_PAGE;

  const paginatedProfiles = profiles.slice(startIndex, endIndex);

  // ---------------------------------------------------------
  // CHECKBOX GROUP
  // ---------------------------------------------------------

  const renderCheckboxGroup = (
    options: { value: string; label: string }[],
    selected: string[],
    setter: React.Dispatch<React.SetStateAction<string[]>>,
    title: string,
  ) => (
    <div className='sidebar-card'>
      <p className='sidebar-title'>{title}</p>

      {options.map(({ value, label }) => (
        <label key={value} className='premium-checkbox'>
          <input type='checkbox' checked={selected.includes(value)} onChange={() => toggleItem(setter, value)} />

          <span>{label}</span>
        </label>
      ))}
    </div>
  );

  // ---------------------------------------------------------
  // UI
  // ---------------------------------------------------------

  return (
    <div className='ml-root'>
      {/* ================= SIDEBAR ================= */}
      {successMessage && (
        <div className='success-toast'>
          <div className='success-toast-icon'>✓</div>

          <div className='success-toast-content'>
            <strong>Interest Sent!</strong>
            <span>{successMessage}</span>
          </div>

          <button className='success-toast-close' onClick={() => setSuccessMessage("")}>
            ×
          </button>
        </div>
      )}
      <aside className={`ml-sidebar ${showFilters ? "show" : ""}`}>
        {/* Clear Filters */}

        {isFilterActive && (
          <div className='sidebar-card'>
            <button className='clear-filter-btn' onClick={clearFilters}>
              ✕ Clear Filters
            </button>
          </div>
        )}

        {/* Sort */}

        <div className='sidebar-card'>
          <label className='sort-label'>Sort By</label>

          <select className='premium-select' value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option>Latest First</option>
            <option>Age: Low to High</option>
            <option>Age: High to Low</option>
          </select>
        </div>

        {/* Gender */}

        <div className='sidebar-card'>
          <p className='sidebar-title'>I'm looking for</p>

          <div className='gender-toggle'>
            <button
              className={`gender-btn ${lookingFor === "male" ? "active" : ""}`}
              onClick={() => setLookingFor(lookingFor === "male" ? null : "male")}
            >
              Male
            </button>

            <button
              className={`gender-btn ${lookingFor === "female" ? "active" : ""}`}
              onClick={() => setLookingFor(lookingFor === "female" ? null : "female")}
            >
              Female
            </button>
          </div>
        </div>

        {/* Age Range */}

        <div className='sidebar-card'>
          <p className='sidebar-title'>Age Range</p>

          <div className='age-range-row'>
            <select value={ageFrom} onChange={(e) => setAgeFrom(Number(e.target.value))}>
              {Array.from({ length: 43 }, (_, i) => i + 18).map((n) => (
                <option key={n}>{n}</option>
              ))}
            </select>

            <span>to</span>

            <select value={ageTo} onChange={(e) => setAgeTo(Number(e.target.value))}>
              {Array.from({ length: 43 }, (_, i) => i + 18).map((n) => (
                <option key={n}>{n}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Marital Status */}

        {renderCheckboxGroup(
          MARITAL_STATUS_OPTIONS,
          selectedMaritalStatuses,
          setSelectedMaritalStatuses,
          "Marital Status",
        )}

        {/* Religion */}

        {renderCheckboxGroup(RELIGION_OPTIONS, selectedReligions, setSelectedReligions, "Religion")}

        {/* Caste */}

        {renderCheckboxGroup(CASTE_OPTIONS, selectedCastes, setSelectedCastes, "Caste")}

        {/* Country */}

        {renderCheckboxGroup(COUNTRY_OPTIONS, selectedCountries, setSelectedCountries, "Country")}
      </aside>

      {/* ================= MAIN CONTENT ================= */}

      <main className='ml-content'>
        {/* Mobile Filter */}

        <div className='mobile-filter-bar'>
          <button className='mobile-filter-btn' onClick={() => setShowFilters(!showFilters)}>
            {showFilters ? "Apply" : "Search"}
          </button>
        </div>

        {/* Header */}

        <div className='content-header'>
          <div>
            <h2>Premium Matches</h2>

            {user && (
              <p>
                Welcome, <b>{user.name}</b>
              </p>
            )}

            <p>Curated profiles tailored for meaningful connections</p>
          </div>
        </div>

        {/* ================= PROFILES ================= */}

        {loading ? (
          <p>Loading profiles...</p>
        ) : profiles.length === 0 ? (
          <p>No profiles found for the selected filters.</p>
        ) : (
          <div className='profile-grid'>
            {paginatedProfiles.map((p: any) => (
              <div key={p.id} className='premium-card'>
                {/* Profile Header */}

                <div className='card-top'>
                  <Avatar
                    sx={{
                      width: 64,
                      height: 64,
                      bgcolor: p.gender === "female" ? "#fde2e4" : "#e3f2fd",
                      fontWeight: 600,
                    }}
                  >
                    {getInitial(p.name)}
                  </Avatar>

                  <div>
                    <h3>{p.name || "Unknown"}</h3>

                    <p className='location'>{p.resident_country}</p>
                  </div>
                </div>

                {/* Profile Information */}

                <div className='card-info'>
                  <span>Age : {calculateAge(p.dob)} yrs</span>

                  <span>Gender : {p.gender}</span>

                  <span>Marital Status : {p.marital_status}</span>

                  <span>Religion : {p.religion}</span>

                  <span>Caste : {p.caste}</span>

                  <span>Education : {p.education}</span>

                  <span>Occupation : {p.occupation}</span>

                  <span>Resident Country : {p.resident_country}</span>
                </div>

                {/* Footer */}

                <div className='card-footer'>
                  <span className='published'>{p.created_at ? new Date(p.created_at).toDateString() : "Recently"}</span>

                  {/* ================= BUTTON ================= */}

                  {hasAccessMap[p.id] ? (
                    <button className='view-btn' onClick={() => handleOpenProfile(p)}>
                      View Profile
                    </button>
                  ) : (
                    <button
                      className='view-btn'
                      onClick={() => handleSendInterest(p.id)}
                      disabled={
                        sendingId === p.id || Number(user?.id) === Number(p.id) || interestStatus[p.id] === "pending"
                      }
                    >
                      {sendingId === p.id
                        ? "Sending..."
                        : interestStatus[p.id] === "pending"
                          ? "Interest Sent ⏳"
                          : "Send Interest ❤️"}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ================= PAGINATION ================= */}

        {totalPages > 1 && (
          <div className='pagination'>
            <button className='page-btn' onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
              «
            </button>

            <button className='page-btn' onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1}>
              &lt;
            </button>

            <span className='page-info'>
              Showing {startIndex + 1} - {Math.min(endIndex, profiles.length)} of {profiles.length}
            </span>

            <button
              className='page-btn'
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              &gt;
            </button>

            <button
              className='page-btn'
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
            >
              »
            </button>
          </div>
        )}
      </main>

      {/* ================= MODALS ================= */}

      <PaymentActivationModal open={openPaymentModal} onClose={() => setOpenPaymentModal(false)} />

      <Notifications />

      <ProfileDetailsModal
        open={openModal}
        onClose={handleCloseProfile}
        profile={selectedProfile}
        calculateAge={calculateAge}
        getInitial={getInitial}
        isAdminView={false}
      />
    </div>
  );
};

export default MatrimonyList;
